// src/pages/PlatformAdminPage.jsx
// ✅ Dashboard Platform Admin — solo per PLATFORM_ADMIN (goumouraphael57@gmail.com)
import React, { useState, useEffect, useCallback } from 'react';
import axiosConfig from '../api/axiosConfig';
import { formatCurrency, formatDate, formatNumber } from '../utils/formatters';
import {
    BuildingOffice2Icon, UsersIcon, CubeIcon, CurrencyEuroIcon,
    SparklesIcon, ChartBarIcon, ShieldCheckIcon, BellIcon,
    ArrowPathIcon, MagnifyingGlassIcon, CheckCircleIcon,
    XCircleIcon, ExclamationTriangleIcon, EyeIcon,
    BanknotesIcon, GlobeAltIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// ==================== API ====================
const api = {
    getDashboard: () => axiosConfig.get('/superadmin/dashboard'),
    getStats: () => axiosConfig.get('/superadmin/stats'),
    getOrgs: (params) => axiosConfig.get('/superadmin/organizations', { params }),
    getOrg: (id) => axiosConfig.get(`/superadmin/organizations/${id}`),
    setPlan: (id, plan, days) => axiosConfig.put(`/superadmin/organizations/${id}/plan`, { plan, durationDays: days }),
    toggle: (id) => axiosConfig.patch(`/superadmin/organizations/${id}/toggle`),
    extendTrial: (id, days) => axiosConfig.post(`/superadmin/organizations/${id}/trial`, { days }),
    notify: (id, data) => axiosConfig.post(`/superadmin/organizations/${id}/notify`, data),
    getAiCosts: (month) => axiosConfig.get('/superadmin/ai-costs', { params: { month } }),
    getRevenue: () => axiosConfig.get('/superadmin/revenue'),
    getUsers: (params) => axiosConfig.get('/superadmin/users', { params }),
};

// ==================== COMPONENTS ====================

const KpiCard = ({ icon: Icon, label, value, sub, color = 'blue' }) => {
    const colors = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-emerald-50 text-emerald-600',
        purple: 'bg-purple-50 text-purple-600',
        orange: 'bg-orange-50 text-orange-600',
        red: 'bg-red-50 text-red-600',
    };
    return (
        <div className="bg-white rounded-2xl border p-5">
            <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <p className="text-sm text-gray-500 font-medium">{label}</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
    );
};

const PlanBadge = ({ plan }) => {
    const colors = {
        FREE: 'bg-gray-100 text-gray-600',
        STARTER: 'bg-blue-100 text-blue-700',
        PRO: 'bg-purple-100 text-purple-700',
        ENTERPRISE: 'bg-amber-100 text-amber-700',
    };
    return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${colors[plan] || colors.FREE}`}>
            {plan}
        </span>
    );
};

const StatusDot = ({ active }) => (
    <span className={`w-2 h-2 rounded-full inline-block ${active ? 'bg-emerald-500' : 'bg-red-400'}`} />
);

// ==================== TABS ====================

const TABS = [
    { id: 'overview', label: 'Overview',        icon: ChartBarIcon },
    { id: 'revenue',  label: 'Revenue / MRR',   icon: BanknotesIcon },
    { id: 'orgs',     label: 'Organizzazioni',  icon: BuildingOffice2Icon },
    { id: 'users',    label: 'Utenti',          icon: UserGroupIcon },
    { id: 'ai',       label: 'AI & Costi',      icon: SparklesIcon },
];

const PLAN_COLORS = {
    FREE: 'bg-gray-100 text-gray-600',
    STARTER: 'bg-sky-100 text-sky-700',
    PRO: 'bg-blue-100 text-blue-700',
    ENTERPRISE: 'bg-purple-100 text-purple-700',
};

const ROLE_COLORS = {
    SUPER_ADMIN: 'bg-yellow-100 text-yellow-800',
    SITE_MANAGER: 'bg-blue-100 text-blue-700',
    ENGINEER: 'bg-emerald-100 text-emerald-700',
    ACCOUNTANT: 'bg-orange-100 text-orange-700',
    FIELD_SUPERVISOR: 'bg-indigo-100 text-indigo-700',
    VIEWER: 'bg-gray-100 text-gray-600',
};

// ==================== MAIN ====================

const PlatformAdminPage = () => {
    const { t } = useTranslation();
    const [tab, setTab] = useState('overview');
    const [dashboard, setDashboard] = useState(null);
    const [stats, setStats] = useState(null);
    const [orgs, setOrgs] = useState([]);
    const [aiCosts, setAiCosts] = useState(null);
    const [revenue, setRevenue] = useState(null);
    const [users, setUsers] = useState([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [userSearch, setUserSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [planFilter, setPlanFilter] = useState('');
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [orgDetail, setOrgDetail] = useState(null);
    const [notifyForm, setNotifyForm] = useState({ title: '', message: '' });
    const [showNotify, setShowNotify] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        const [d, s, o, ai, rev, u] = await Promise.allSettled([
            api.getDashboard(),
            api.getStats(),
            api.getOrgs({ limit: 50 }),
            api.getAiCosts(),
            api.getRevenue(),
            api.getUsers({ limit: 50 }),
        ]);

        if (d.status === 'fulfilled') setDashboard(d.value.data.data);
        else toast.error('Errore: dashboard globale');

        if (s.status === 'fulfilled') setStats(s.value.data.data);
        else toast.error('Errore: statistiche piattaforma');

        if (o.status === 'fulfilled') setOrgs(o.value.data.data || []);
        else toast.error('Errore: lista organizzazioni');

        if (ai.status === 'fulfilled') setAiCosts(ai.value.data.data);
        else toast.error('Errore: costi AI');

        if (rev.status === 'fulfilled') setRevenue(rev.value.data.data);
        else toast.error('Errore: revenue/MRR');

        if (u.status === 'fulfilled') {
            setUsers(u.value.data.data?.users || []);
            setUsersTotal(u.value.data.data?.total || 0);
        } else toast.error('Errore: utenti globali');

        setLoading(false);
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const loadOrgDetail = async (id) => {
        try {
            const res = await api.getOrg(id);
            setOrgDetail(res.data.data);
            setSelectedOrg(id);
        } catch {
            toast.error('Errore caricamento org');
        }
    };

    const handleSetPlan = async (id, plan) => {
        try {
            await api.setPlan(id, plan, 365);
            toast.success(`Piano ${plan} assegnato`);
            loadAll();
            if (selectedOrg === id) loadOrgDetail(id);
        } catch {
            toast.error('Errore cambio piano');
        }
    };

    const handleToggle = async (id) => {
        try {
            const res = await api.toggle(id);
            toast.success(res.data.message);
            loadAll();
        } catch {
            toast.error('Errore toggle org');
        }
    };

    const handleExtendTrial = async (id) => {
        try {
            await api.extendTrial(id, 14);
            toast.success('Trial esteso di 14 giorni');
            loadAll();
        } catch {
            toast.error('Errore estensione trial');
        }
    };

    const handleNotify = async (id) => {
        if (!notifyForm.title || !notifyForm.message) {
            toast.error('Titolo e messaggio richiesti');
            return;
        }
        try {
            const res = await api.notify(id, { ...notifyForm, type: 'info' });
            toast.success(res.data.message);
            setShowNotify(false);
            setNotifyForm({ title: '', message: '' });
        } catch {
            toast.error('Errore invio notifica');
        }
    };

    const filteredOrgs = orgs.filter(o => {
        const matchSearch = !search || o.name.toLowerCase().includes(search.toLowerCase()) ||
            o.email?.toLowerCase().includes(search.toLowerCase());
        const matchPlan = !planFilter || o.plan === planFilter;
        return matchSearch && matchPlan;
    });

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <ArrowPathIcon className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
    );

    const d = dashboard;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <ShieldCheckIcon className="w-7 h-7 text-red-500" />
                        Platform Admin
                    </h1>
                    <p className="text-gray-500 text-sm mt-0.5">Pannello controllo globale InfraPilot</p>
                </div>
                <button onClick={loadAll} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm hover:bg-gray-50 transition">
                    <ArrowPathIcon className="w-4 h-4" /> Aggiorna
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 gap-1">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${tab === t.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                        <t.icon className="w-4 h-4" /> {t.label}
                    </button>
                ))}
            </div>

            {/* ── TAB OVERVIEW ── */}
            {tab === 'overview' && d && (
                <div className="space-y-6">
                    {/* KPI */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <KpiCard icon={BuildingOffice2Icon} label="Organizzazioni totali" value={d.summary.totalOrganizations} sub={`${d.summary.activeOrganizations} attive`} color="blue" />
                        <KpiCard icon={UsersIcon} label="Utenti attivi" value={formatNumber(d.summary.totalUsers)} color="green" />
                        <KpiCard icon={CubeIcon} label="Progetti totali" value={formatNumber(d.summary.totalProjects)} color="purple" />
                        <KpiCard icon={CurrencyEuroIcon} label="Spese piattaforma" value={`€${formatNumber(d.summary.totalExpenses)}`} sub={`${d.summary.expenseCount} registrate`} color="orange" />
                    </div>

                    {/* Stats crescita */}
                    {stats && (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <KpiCard icon={ChartBarIcon} label="Nuovi 7 giorni" value={stats.growth.last7days} color="green" />
                            <KpiCard icon={ChartBarIcon} label="Nuovi 30 giorni" value={stats.growth.last30days} color="blue" />
                            <KpiCard icon={CheckCircleIcon} label="Abbonamenti paganti" value={stats.subscriptions.paid} color="purple" />
                            <KpiCard icon={ExclamationTriangleIcon} label="Trial attivi" value={stats.subscriptions.trials} color="orange" />
                        </div>
                    )}

                    {/* Distribuzione piani */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl border p-5">
                            <h3 className="font-bold text-gray-900 mb-4">Distribuzione Piani</h3>
                            <div className="space-y-3">
                                {d.breakdown.byPlan.map(p => (
                                    <div key={p.plan} className="flex items-center justify-between">
                                        <PlanBadge plan={p.plan} />
                                        <div className="flex items-center gap-3 flex-1 mx-4">
                                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full bg-blue-500 rounded-full"
                                                    style={{ width: `${(p._count / d.summary.totalOrganizations) * 100}%` }} />
                                            </div>
                                        </div>
                                        <span className="text-sm font-bold text-gray-700">{p._count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border p-5">
                            <h3 className="font-bold text-gray-900 mb-4">Ultime Organizzazioni</h3>
                            <div className="space-y-2">
                                {d.recentOrganizations.slice(0, 5).map(o => (
                                    <div key={o.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">{o.name}</p>
                                            <p className="text-xs text-gray-400">{o.country} · {formatDate(o.createdAt)}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <PlanBadge plan={o.plan} />
                                            <StatusDot active={o.isActive} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── TAB REVENUE / MRR ── */}
            {tab === 'revenue' && revenue && (
                <div className="space-y-6">
                    {/* KPI Revenue */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <KpiCard icon={BanknotesIcon} label="MRR" value={`€${formatNumber(revenue.mrr)}`} sub="Entrate mensili ricorrenti" color="green" />
                        <KpiCard icon={ChartBarIcon} label="ARR" value={`€${formatNumber(revenue.arr)}`} sub="Entrate annuali ricorrenti" color="blue" />
                        <KpiCard icon={UsersIcon} label="Abbonamenti paganti" value={revenue.paidSubscriptions} sub="Esclude piano FREE" color="purple" />
                        <KpiCard icon={UsersIcon} label="Utenti attivi" value={formatNumber(revenue.totalActiveUsers)} color="orange" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Revenue per piano */}
                        <div className="bg-white rounded-2xl border p-5">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <BanknotesIcon className="w-5 h-5 text-green-500" />
                                Revenue per Piano (MRR)
                            </h3>
                            <div className="space-y-4">
                                {revenue.byPlan.map(p => (
                                    <div key={p.plan} className="space-y-1">
                                        <div className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${PLAN_COLORS[p.plan] || 'bg-gray-100'}`}>{p.plan}</span>
                                                <span className="text-gray-500">{p.count} org</span>
                                            </div>
                                            <span className="font-bold text-gray-900">€{formatNumber(p.mrrEur)}/mese</span>
                                        </div>
                                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
                                                style={{ width: `${revenue.mrr > 0 ? (p.mrrEur / revenue.mrr) * 100 : 0}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t flex justify-between text-sm">
                                <span className="font-semibold text-gray-700">MRR totale</span>
                                <span className="font-bold text-green-600 text-lg">€{formatNumber(revenue.mrr)}</span>
                            </div>
                        </div>

                        {/* Distribuzione per regione */}
                        <div className="bg-white rounded-2xl border p-5">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <GlobeAltIcon className="w-5 h-5 text-blue-500" />
                                Distribuzione per Regione
                            </h3>
                            <div className="space-y-3">
                                {Object.entries(revenue.byRegion || {}).map(([region, count]) => (
                                    <div key={region} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg">{region === 'EUROPE' ? '🇪🇺' : region === 'WEST_AFRICA' ? '🌍' : '🌐'}</span>
                                            <span className="text-sm font-medium text-gray-700">{region === 'EUROPE' ? 'Europa' : region === 'WEST_AFRICA' ? 'Africa Occidentale' : region}</span>
                                        </div>
                                        <span className="font-bold text-gray-900">{count}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Ultimi iscritti */}
                            <div className="mt-5 pt-4 border-t">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Ultimi 30 giorni</p>
                                <div className="space-y-2">
                                    {(revenue.recentSignups || []).slice(0, 5).map(org => (
                                        <div key={org.id} className="flex items-center justify-between text-xs">
                                            <div className="flex items-center gap-2">
                                                <span>{org.country === 'GN' ? '🇬🇳' : org.country === 'SN' ? '🇸🇳' : org.country === 'IT' ? '🇮🇹' : org.country === 'FR' ? '🇫🇷' : '🌐'}</span>
                                                <span className="font-medium text-gray-700 truncate max-w-32">{org.name}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${PLAN_COLORS[org.plan] || 'bg-gray-100'}`}>{org.plan}</span>
                                                <span className="text-gray-400">{formatDate(org.createdAt)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── TAB UTENTI GLOBALI ── */}
            {tab === 'users' && (
                <div className="space-y-4">
                    {/* Barra ricerca */}
                    <div className="bg-white rounded-2xl border p-4 flex flex-wrap gap-3 items-center">
                        <div className="relative flex-1 min-w-48">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)}
                                placeholder="Cerca utente per nome o email..."
                                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <span className="text-sm text-gray-400">{usersTotal} utenti totali</span>
                    </div>

                    {/* Tabella utenti */}
                    <div className="bg-white rounded-2xl border overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Utente</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('settings.organization')}</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ruolo</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Piano</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Registrato</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {users
                                    .filter(u => !userSearch ||
                                        u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
                                        u.firstName?.toLowerCase().includes(userSearch.toLowerCase()) ||
                                        u.lastName?.toLowerCase().includes(userSearch.toLowerCase()))
                                    .map(u => (
                                    <tr key={u.id} className="hover:bg-gray-50 transition">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                                    {u.firstName?.[0]}{u.lastName?.[0]}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{u.firstName} {u.lastName}</p>
                                                    <p className="text-xs text-gray-400">{u.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-gray-700 text-xs">{u.organization?.name || '—'}</p>
                                            <p className="text-xs text-gray-400">{u.organization?.country}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${PLAN_COLORS[u.organization?.plan] || 'bg-gray-100 text-gray-600'}`}>
                                                {u.organization?.plan || '—'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <StatusDot active={u.isActive} />
                                                <span className={`text-xs ${u.isActive ? 'text-emerald-600' : 'text-red-500'}`}>
                                                    {u.isActive ? 'Attivo' : 'Inattivo'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-400">
                                            {formatDate(u.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── TAB ORGANIZZAZIONI ── */}
            {tab === 'orgs' && (
                <div className="space-y-4">
                    {/* Filtri */}
                    <div className="bg-white rounded-2xl border p-4 flex flex-wrap gap-3">
                        <div className="relative flex-1 min-w-48">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                                placeholder="Cerca organizzazione..."
                                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        </div>
                        <select value={planFilter} onChange={e => setPlanFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50">
                            <option value="">Tutti i piani</option>
                            {['FREE', 'STARTER', 'PRO', 'ENTERPRISE'].map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                        <span className="text-sm text-gray-400 self-center">{filteredOrgs.length} organizzazioni</span>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Lista org */}
                        <div className="bg-white rounded-2xl border overflow-hidden">
                            <div className="divide-y divide-gray-50">
                                {filteredOrgs.map(org => (
                                    <div key={org.id}
                                        onClick={() => loadOrgDetail(org.id)}
                                        className={`p-4 cursor-pointer hover:bg-gray-50 transition ${selectedOrg === org.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <StatusDot active={org.isActive} />
                                                    <p className="text-sm font-semibold text-gray-900 truncate">{org.name}</p>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-0.5">{org.country} · {org.region} · {formatDate(org.createdAt)}</p>
                                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                                    <span>👥 {org._count?.users || 0}</span>
                                                    <span>📋 {org._count?.projects || 0}</span>
                                                    <span>💰 {org._count?.expenses || 0}</span>
                                                </div>
                                            </div>
                                            <PlanBadge plan={org.plan} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Dettaglio org selezionata */}
                        {orgDetail ? (
                            <div className="bg-white rounded-2xl border p-5 space-y-4">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="font-bold text-gray-900">{orgDetail.name}</h3>
                                        <p className="text-xs text-gray-400">{orgDetail.slug}</p>
                                    </div>
                                    <PlanBadge plan={orgDetail.plan} />
                                </div>

                                {/* Info */}
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    {[
                                        ['Paese', orgDetail.country],
                                        ['Regione', orgDetail.region],
                                        ['Valuta', orgDetail.currency],
                                        ['Utenti', orgDetail._count?.users],
                                        ['Progetti', orgDetail._count?.projects],
                                        ['Spese', orgDetail._count?.expenses],
                                        ['Status sub', orgDetail.subscription?.status],
                                        ['Trial', orgDetail.subscription?.trialEndsAt ? formatDate(orgDetail.subscription.trialEndsAt) : 'N/A'],
                                    ].map(([k, v]) => (
                                        <div key={k} className="flex justify-between py-1 border-b border-gray-50">
                                            <span className="text-gray-400">{k}</span>
                                            <span className="font-medium text-gray-700">{v || '-'}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Azioni */}
                                <div className="space-y-2">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.actions')}</p>

                                    {/* Cambio piano */}
                                    <div className="flex flex-wrap gap-1.5">
                                        {['FREE', 'STARTER', 'PRO', 'ENTERPRISE'].map(p => (
                                            <button key={p} onClick={() => handleSetPlan(orgDetail.id, p)}
                                                className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${orgDetail.plan === p ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                                {p}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Altre azioni */}
                                    <div className="flex flex-wrap gap-2">
                                        <button onClick={() => handleToggle(orgDetail.id)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition ${orgDetail.isActive ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}>
                                            {orgDetail.isActive ? <><XCircleIcon className="w-3.5 h-3.5" /> Blocca</> : <><CheckCircleIcon className="w-3.5 h-3.5" /> Attiva</>}
                                        </button>
                                        <button onClick={() => handleExtendTrial(orgDetail.id)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl text-xs font-semibold transition">
                                            +14gg Trial
                                        </button>
                                        <button onClick={() => setShowNotify(true)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-600 hover:bg-purple-100 rounded-xl text-xs font-semibold transition">
                                            <BellIcon className="w-3.5 h-3.5" /> Notifica
                                        </button>
                                    </div>
                                </div>

                                {/* Form notifica */}
                                {showNotify && (
                                    <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 space-y-2">
                                        <p className="text-xs font-semibold text-purple-700">Invia notifica agli admin</p>
                                        <input type="text" placeholder="Titolo" value={notifyForm.title}
                                            onChange={e => setNotifyForm(p => ({ ...p, title: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                        <textarea placeholder="Messaggio" value={notifyForm.message}
                                            onChange={e => setNotifyForm(p => ({ ...p, message: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none" rows={2} />
                                        <div className="flex gap-2">
                                            <button onClick={() => handleNotify(orgDetail.id)}
                                                className="flex-1 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 transition">
                                                Invia
                                            </button>
                                            <button onClick={() => setShowNotify(false)}
                                                className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition">
                                                Annulla
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Utenti */}
                                {orgDetail.users && orgDetail.users.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Utenti</p>
                                        <div className="space-y-1">
                                            {orgDetail.users.slice(0, 5).map(u => (
                                                <div key={u.id} className="flex items-center justify-between text-xs py-1">
                                                    <div className="flex items-center gap-2">
                                                        <StatusDot active={u.isActive} />
                                                        <span className="font-medium text-gray-700">{u.firstName} {u.lastName}</span>
                                                    </div>
                                                    <span className="text-gray-400">{u.role}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl border p-10 text-center text-gray-400">
                                <EyeIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                <p className="text-sm">Seleziona un'organizzazione per i dettagli</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── TAB AI & COSTI ── */}
            {tab === 'ai' && aiCosts && (
                <div className="space-y-5">
                    {/* Totali */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <KpiCard icon={SparklesIcon} label="Richieste AI totali" value={formatNumber(aiCosts.totals.requests)} color="purple" />
                        <KpiCard icon={ChartBarIcon} label="Token totali" value={formatNumber(aiCosts.totals.tokens)} color="blue" />
                        <KpiCard icon={CurrencyEuroIcon} label="Costo USD" value={`$${aiCosts.totals.costUsd}`} color="orange" />
                        <KpiCard icon={CurrencyEuroIcon} label="Costo EUR" value={`€${aiCosts.totals.costEur}`} color="red" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Per organizzazione */}
                        <div className="bg-white rounded-2xl border p-5">
                            <h3 className="font-bold text-gray-900 mb-4">Costi per Organizzazione</h3>
                            <div className="space-y-3">
                                {aiCosts.byOrganization.slice(0, 10).map((o, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0 mr-3">
                                            <p className="text-sm font-medium text-gray-900 truncate">{o.organization?.name || 'N/A'}</p>
                                            <p className="text-xs text-gray-400">{o.requests} richieste · {formatNumber(o.tokens)} token</p>
                                        </div>
                                        <span className="text-sm font-bold text-orange-600 flex-shrink-0">${o.costUsd}</span>
                                    </div>
                                ))}
                                {aiCosts.byOrganization.length === 0 && (
                                    <p className="text-sm text-gray-400 text-center py-4">Nessun dato questo mese</p>
                                )}
                            </div>
                        </div>

                        {/* Per feature */}
                        <div className="bg-white rounded-2xl border p-5">
                            <h3 className="font-bold text-gray-900 mb-4">Costi per Feature</h3>
                            <div className="space-y-3">
                                {aiCosts.byFeature.map((f, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex-1 mr-3">
                                            <p className="text-sm font-medium text-gray-900 capitalize">{f.feature}</p>
                                            <p className="text-xs text-gray-400">{f._count} richieste</p>
                                        </div>
                                        <span className="text-sm font-bold text-purple-600">${Number((f._sum?.costUsd || 0).toFixed(4))}</span>
                                    </div>
                                ))}
                                {aiCosts.byFeature.length === 0 && (
                                    <p className="text-sm text-gray-400 text-center py-4">Nessun dato questo mese</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlatformAdminPage;